<template>
  <div 
    ref="carouselContainer"
    class="infinite-carousel"
    tabindex="0"
    role="region"
    :aria-label="carouselLabel"
    @keydown.enter="openLightbox(0)"
    @keydown.space.prevent="openLightbox(0)"
  >
    <Splide 
      :options="options" 
      :extensions="extensions"
      :aria-label="carouselLabel"
      @splide:mounted="disableArrowTabbing"
      @splide:updated="disableArrowTabbing"
    >
      <SplideSlide v-for="(image, index) in images" :key="index">
        <div 
          class="carousel-item" 
          @click="openLightbox(index)"
        >
          <img 
            :data-splide-lazy="resolveImageUrl(image.url)" 
            :src="resolveImageUrl(image.url)" 
            :alt="image.alt || 'Carousel image'" 
            loading="lazy" 
            decoding="async"
          >
        </div>
      </SplideSlide>
    </Splide>

    <!-- Lightbox Modal Overlay -->
    <Teleport to="body">
      <Transition name="lightbox-fade">
        <div 
          v-if="selectedIndex !== null && activeImage" 
          class="lightbox-overlay"
          @click="closeLightbox"
          @touchstart.passive="handleTouchStart"
          @touchend.passive="handleTouchEnd"
          role="dialog"
          aria-modal="true"
          :aria-label="activeImage.alt || 'Expanded Image View'"
        >
          <!-- Close Button -->
          <button 
            class="lightbox-close" 
            @click.stop="closeLightbox" 
            aria-label="Close image modal"
            type="button"
          >
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          <!-- Previous Button (fixed screen position) -->
          <button 
            class="lightbox-nav lightbox-nav--prev" 
            @click.stop="prevImage" 
            aria-label="Previous image"
            type="button"
          >
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>

          <div class="lightbox-content">
            <!-- Main Media & Caption -->
            <div class="lightbox-media-wrapper">
              <img 
                :src="resolveImageUrl(activeImage.url)" 
                :alt="activeImage.alt || 'Carousel image'" 
                class="lightbox-image"
                decoding="async"
                @click.stop
              />
              <div v-if="activeImage.alt" class="lightbox-caption" @click.stop>
                <p>{{ activeImage.alt }}</p>
              </div>
            </div>
          </div>

          <!-- Next Button (fixed screen position) -->
          <button 
            class="lightbox-nav lightbox-nav--next" 
            @click.stop="nextImage" 
            aria-label="Next image"
            type="button"
          >
            <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script>
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import { AutoScroll } from '@splidejs/splide-extension-auto-scroll';
import '@splidejs/vue-splide/css';

export default {
  name: 'InfiniteCarousel',
  components: {
    Splide,
    SplideSlide,
  },
  props: {
    name: {
      type: String,
      default: 'Gallery',
    },
    images: {
      type: Array,
      required: true,
      default: () => [],
    },
    speed: {
      type: Number,
      default: 0.7,
    }
  },
  data() {
    return {
      extensions: {
        AutoScroll,
      },
      selectedIndex: null,
      touchStartX: 0,
      touchStartY: 0,
    };
  },
  computed: {
    carouselLabel() {
      return `${this.name} image carousel`;
    },
    options() {
      return {
        type: 'loop',
        drag: 'free',
        focus: 'center',
        autoWidth: true,
        gap: '2rem',
        arrows: true,
        keyboard: false,
        pagination: false,
        lazyLoad: 'nearby',
        autoScroll: {
          speed: this.speed,
          pauseOnHover: true,
          pauseOnFocus: true,
          rewind: false,
        },
      };
    },
    activeImage() {
      if (this.selectedIndex === null || !this.images || !this.images.length) return null;
      return this.images[this.selectedIndex];
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyDown);
    this.$nextTick(() => {
      this.disableArrowTabbing();
    });
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
    if (this.selectedIndex !== null) {
      document.body.style.overflow = '';
    }
  },
  methods: {
    disableArrowTabbing() {
      if (this.$refs.carouselContainer) {
        const arrows = this.$refs.carouselContainer.querySelectorAll('.splide__arrow');
        arrows.forEach((arrow) => {
          arrow.setAttribute('tabindex', '-1');
          arrow.setAttribute('aria-hidden', 'true');
        });
      }
    },
    openLightbox(index) {
      this.selectedIndex = index;
      document.body.style.overflow = 'hidden';
    },
    closeLightbox() {
      this.selectedIndex = null;
      document.body.style.overflow = '';
      this.$nextTick(() => {
        if (this.$refs.carouselContainer) {
          this.$refs.carouselContainer.focus();
        }
      });
    },
    prevImage() {
      if (this.selectedIndex !== null && this.images.length > 0) {
        this.selectedIndex = (this.selectedIndex - 1 + this.images.length) % this.images.length;
      }
    },
    nextImage() {
      if (this.selectedIndex !== null && this.images.length > 0) {
        this.selectedIndex = (this.selectedIndex + 1) % this.images.length;
      }
    },
    handleKeyDown(event) {
      if (this.selectedIndex === null) return;
      if (event.key === 'Escape') {
        this.closeLightbox();
      } else if (event.key === 'ArrowLeft') {
        this.prevImage();
      } else if (event.key === 'ArrowRight') {
        this.nextImage();
      }
    },
    handleTouchStart(event) {
      if (event.touches && event.touches.length === 1) {
        this.touchStartX = event.touches[0].clientX;
        this.touchStartY = event.touches[0].clientY;
      }
    },
    handleTouchEnd(event) {
      if (event.changedTouches && event.changedTouches.length === 1) {
        const deltaX = event.changedTouches[0].clientX - this.touchStartX;
        const deltaY = event.changedTouches[0].clientY - this.touchStartY;
        const minSwipeDistance = 40; // minimum swipe distance in px

        // Verify gesture is primarily horizontal to avoid triggering during vertical adjustments
        if (Math.abs(deltaX) > minSwipeDistance && Math.abs(deltaX) > Math.abs(deltaY)) {
          if (deltaX < 0) {
            this.nextImage(); // Swiped left -> next
          } else {
            this.prevImage(); // Swiped right -> prev
          }
        }
      }
    },
    resolveImageUrl(url) {
      if (!url) return '';
      const trimmed = url.trim();
      if (/^(https?:|\/|data:)/i.test(trimmed)) {
        return trimmed;
      }
      if (trimmed.startsWith('gallery/') || trimmed.startsWith('new_gallery/')) {
        return `/${trimmed}`;
      }
      return `/gallery/${trimmed}`;
    }
  }
};
</script>

<style lang="scss">
.infinite-carousel {
  width: 100vw;
  margin: 1rem 0;
  overflow: hidden;

  .splide__track {
    overflow: visible; 
  }

  // Style and position the arrows overlayed on the left/right sides of the screen
  .splide__arrow {
    opacity: 0;
    transition: opacity var(--transition-smooth), background-color var(--transition-smooth), transform var(--transition-fast), border-color var(--transition-smooth);
    background: rgba(24, 223, 223, 0.75); // glassmorphic dark backdrop matching the site's dark color
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255, 255, 255, 0.15);
    pointer-events: auto; // Allow mouse interactions
    cursor: pointer;
    width: 3rem;
    height: 3rem;
    border-radius: var(--border-radius-full);
    display: flex;
    justify-content: center;
    align-items: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    z-index: 10;
    
    &--prev {
      left: 4vw;
    }
    
    &--next {
      right: 4vw;
    }

    &:hover {
      opacity: 1 !important;
      background: var(--regular);
      transform: translateY(-50%) scale(1.1);
      border-color: rgba(255, 255, 255, 0.35);
    }

    &:focus {
      outline: none;
    }
    
    svg {
      fill: var(--white);
      width: 1.2rem;
      height: 1.2rem;
    }
  }

  // Fade arrows in when hovering the carousel container
  &:hover {
    .splide__arrow {
      opacity: 0.75;
    }
  }

  // Ensure visibility on touch devices (no hover support)
  @media (hover: none) {
    .splide__arrow {
      opacity: 0.75;
    }
  }

  .carousel-item {
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    
    img {
      width: auto;
      height: 400px;
      max-width: 80vw;
      object-fit: cover;
      border-radius: var(--border-radius-lg);
      transition: filter var(--transition-smooth), transform var(--transition-fast);

      @media (max-width: 768px) {
        height: 300px;
      }
    }

    &:hover img {
      filter: brightness(1.1);
      transform: scale(1.02);
    }
  }
}

// Lightbox Overlay Styles (un-nested for Teleport to body)
.lightbox-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 21, 27, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  padding: 1.5rem;
  box-sizing: border-box;
  user-select: none;
  font-family: var(--font-body);

  @media (max-width: 768px) {
    padding: 1rem 0.75rem 5.25rem 0.75rem; // Leave space at bottom for nav buttons
  }
}

.lightbox-close {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  width: 3rem;
  height: 3rem;
  border-radius: var(--border-radius-full);
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: var(--white);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: background-color var(--transition-fast), border-color var(--transition-fast), transform var(--transition-fast);
  z-index: 100001;

  &:hover {
    background: var(--regular);
    transform: scale(1.1);
    border-color: rgba(255, 255, 255, 0.4);
  }

  @media (max-width: 768px) {
    top: 1rem;
    right: 1rem;
    width: 2.75rem;
    height: 2.75rem;
  }

  svg {
    stroke: var(--white);
  }
}

.lightbox-content {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 1400px;
  max-height: 94vh;
  z-index: 100000;
  pointer-events: none;
  padding: 0 4.5rem;

  @media (max-width: 768px) {
    padding: 0;
    max-height: 82vh;
  }
}

.lightbox-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 100002;
  background: rgba(2, 151, 157, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  width: 3.5rem;
  height: 3.5rem;
  border-radius: var(--border-radius-full);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--white);
  transition: background-color var(--transition-smooth), transform var(--transition-fast), border-color var(--transition-smooth);
  flex-shrink: 0;
  pointer-events: auto;

  &--prev {
    left: 2rem;
  }
  &--next {
    right: 2rem;
  }

  &:hover {
    background: var(--regular);
    transform: translateY(-50%) scale(1.1);
    border-color: rgba(255, 255, 255, 0.4);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  // On mobile, position buttons at the bottom center below the image
  @media (max-width: 768px) {
    top: auto;
    bottom: 12rem;
    transform: none;
    width: 3.25rem;
    height: 3.25rem;
    background: rgba(2, 151, 157, 0.45);

    &--prev {
      left: calc(50% - 4.25rem);
    }
    &--next {
      right: calc(50% - 4.25rem);
      left: auto;
    }

    &:hover {
      transform: scale(1.1);
    }

    &:active {
      transform: scale(0.95);
    }
  }

  svg {
    stroke: var(--white);
  }
}

.lightbox-media-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: min(88vw, 1150px);
  max-height: 90vh;
  pointer-events: none;

  @media (max-width: 768px) {
    max-width: 96vw;
    max-height: 82vh;
  }
}

.lightbox-image {
  max-width: 100%;
  max-height: 78vh;
  object-fit: contain;
  border-radius: var(--border-radius-lg);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.15);
  pointer-events: auto;

  @media (max-width: 768px) {
    max-width: 96vw;
    max-height: 65vh;
  }
}

.lightbox-caption {
  margin-top: 1rem;
  color: rgba(255, 255, 255, 0.95);
  font-family: var(--font-body);
  font-size: 1.05rem;
  text-align: center;
  max-width: 100%;
  background: rgba(1, 47, 80, 0.8);
  padding: 0.75rem 1.5rem;
  border-radius: var(--border-radius-md);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  line-height: 1.5;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  pointer-events: auto;

  @media (max-width: 768px) {
    margin-top: 0.75rem;
    font-size: 0.95rem;
    padding: 0.5rem 1rem;
    max-width: 96vw;
  }

  p {
    margin: 0;
  }
}

// Lightbox Transition
.lightbox-fade-enter-active,
.lightbox-fade-leave-active {
  transition: opacity var(--transition-smooth);
  .lightbox-media-wrapper {
    transition: transform var(--transition-smooth) cubic-bezier(0.34, 1.56, 0.64, 1);
  }
}

.lightbox-fade-enter-from,
.lightbox-fade-leave-to {
  opacity: 0;
  .lightbox-media-wrapper {
    transform: scale(0.92);
  }
}
</style>

